import { z } from 'zod';

import { IdentifierSchema, NonEmptyTextSchema } from './common.js';

export const ApiErrorCodeSchema = z.enum([
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'validation_failed',
  'rate_limited',
  'gone',
  'internal_error',
  'service_unavailable',
]);

export const ApiErrorSchema = z
  .object({
    code: ApiErrorCodeSchema,
    message: NonEmptyTextSchema,
    fieldErrors: z.record(z.string(), z.array(NonEmptyTextSchema)).optional(),
  })
  .strict();

export const ApiErrorResponseSchema = z
  .object({
    ok: z.literal(false),
    error: ApiErrorSchema,
    requestId: IdentifierSchema.optional(),
  })
  .strict();

export const CursorPageSchema = z
  .object({
    nextCursor: z.string().min(1).optional(),
    hasMore: z.boolean(),
  })
  .strict();

export function createApiSuccessSchema<TSchema extends z.ZodType>(dataSchema: TSchema) {
  return z
    .object({
      ok: z.literal(true),
      data: dataSchema,
      requestId: IdentifierSchema.optional(),
    })
    .strict();
}

export function createApiEnvelopeSchema<TSchema extends z.ZodType>(dataSchema: TSchema) {
  return z.union([createApiSuccessSchema(dataSchema), ApiErrorResponseSchema]);
}

export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;
export type ApiError = z.infer<typeof ApiErrorSchema>;
export type ApiErrorResponse = z.infer<typeof ApiErrorResponseSchema>;
export type CursorPage = z.infer<typeof CursorPageSchema>;
export type ApiSuccess<T> = {
  ok: true;
  data: T;
  requestId?: string;
};
export type ApiEnvelope<T> = ApiSuccess<T> | ApiErrorResponse;
